import {
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import {
  getActivosByFilters,
  getActivosByFiltersParamsProps,
  getActivosExportarExcel,
} from "api/Activo.service"
import { GetActivosByFiltersDtoProps } from "api/DTOs/GetActivosByFiltersDto"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { handleDirectDownloadExcel } from "common/context/DownloadFileProvider"
import notification from "common/utils/notification"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { useFilterMenu } from "hooks/useFilterMenu"
import { DataTableRowClickEventParams } from "primereact/datatable"
import { useCallback, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { backendDateFormat } from "utils/formatDate"
import {
  DateRangeModalFechas,
  ModalFechas,
} from "../LibroDiario/components/Modales/ModalFechas"
import { FilterMenu } from "./FilterMenu"
import { ModalCrearActivo } from "./components/ModalCrearActivo"
import { ACTIVOS_COLUMNS } from "./constants/ActivosColumns"

export const Activos = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  // TODO: Descomentar cuando esté el back:
  // const [selection, setSelection] = useState<GetActivosByFiltersDtoProps[]>([])

  const dtRef = useRef<DataTablePrimeReact<
    GetActivosByFiltersDtoProps[]
  > | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }

  const onRowClick = async ({
    data,
    originalEvent: { target },
  }: DataTableRowClickEventParams) => {
    const cell = target as HTMLTableCellElement
    if (!data.id || !cell?.cellIndex) return

    const { id } = data
    history.push(`${ROUTES.ACTIVOS}/${id}`)
  }
  const [showExportDatesModal, setShowExportDatesModal] = useState(false)
  const currentYear = new Date().getFullYear()
  const fechaLowerLimit = new Date(currentYear, 0, 1, 0, 0, 0, 0)
  const fechaUpperLimit = new Date(currentYear, 11, 31, 23, 59, 59, 999)
  const DEFAULT_DATE = {
    min: fechaLowerLimit,
    max: fechaUpperLimit,
  }
  const [date, setDate] = useState<DateRangeModalFechas>(DEFAULT_DATE)

  const fetchActivos = useCallback(
    () => (queryParams: getActivosByFiltersParamsProps) =>
      getActivosByFilters({
        ...queryParams,
        SkipCount: queryParams.SkipCount ?? 0,
        SortingCriteria: queryParams.SortingCriteria,
        MaxResultCount: queryParams.MaxResultCount ?? 10,
      }),
    []
  )

  const exportToExcel = async () => {
    const { data } = await getActivosExportarExcel({
      FechaLowerLimit: date?.min ? backendDateFormat(date.min) : "",
      FechaUpperLimit: date?.max ? backendDateFormat(date.max) : "",
    })
    handleDirectDownloadExcel(data, `${t("exportarXlxs.activos")}.xlsx`)
    setDate(DEFAULT_DATE)
  }

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchActivos,
    })

  return (
    <section className='tw-flex tw-flex-col tw-order-2 tw-z-10 tw-relative tw-w-full tw-max-h-screen tw-h-full tw-max-w-screen tw-bg-gray tw-overflow-x-hidden tw-overflow-y-hidden'>
      <div className='tw-flex-none tw-bg-white tw-shadow-md tw-h-full'>
        <FilterMenu
          serviceQuery={setServiceQuery}
          createActivo={() => setIsModalOpen(true)}
          sortingCriteria={sortingCriteria}
          exportToExcel={() => setShowExportDatesModal(true)}
          maxResultCount={pagination.itemsPerPage}
          skipCount={pagination.skipCount}
          resetPagination={handleResetPagination}
        />
        <div className='tw-flex tw-flex-col tw-h-[calc(100%-140px)] tw-relative '>
          <div className='tw-h-full tw-min-h-0'>
            <TableERP
              refTable={dtRef}
              columns={ACTIVOS_COLUMNS(t)}
              className='tw-h-full !tw-z-0'
              columnResizeMode='expand'
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={onSort}
              removableSort
              value={items}
              showGridlines
              lazy
              dataKey='id'
              scrollable
              size='small'
              onRowClick={onRowClick}
              // TODO: Descomentar cuando esté el back:
              // selection={selection}
              // selectionMode='checkbox'
              // selectionColumnMode='multiple'
              // onSelectionChange={({
              //   value,
              // }: {
              //   originalEvent: React.SyntheticEvent
              //   value: GetActivosByFiltersDtoProps[]
              //   type?: "multiple" | "all" | "checkbox" | "row" | undefined
              // }) => {
              //   setSelection(value)
              // }}
              loading={isLoading}
              emptyMessage={t("primeReact.table.emptyMessage")}
            />
            <PaginationERP
              className={PAGINATION_STYLES}
              totalRecords={totalCount}
              first={pagination.skipCount}
              rows={pagination.itemsPerPage}
              resetScroll={() => {
                dtRef.current && dtRef.current?.resetScroll()
              }}
              setPagination={setPagination}
              optionDropdown={[10, 20, 50, 100]}
              texts={{
                textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
                textCurrentPageReport: `${t("pagination.from")}`,
              }}
            />
          </div>
        </div>
        <ModalCrearActivo
          isOpen={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          onSuccess={() => {
            notification.success(undefined)
            setIsModalOpen(false)
            fetchTableData()
          }}
        />
        <ModalFechas
          isOpen={showExportDatesModal}
          date={date}
          setDate={setDate}
          onCancel={() => setShowExportDatesModal(false)}
          onConfirm={exportToExcel}
          text={
            <Trans
              i18nKey='nsAssets.exportText'
              components={[
                <span
                  className='tw-font-medium tw-contents'
                  key='0'
                />,
              ]}
            />
          }
        />
      </div>
    </section>
  )
}
